import React, { useCallback, useMemo } from "react";
import { SelectorDisplayItemProps } from "../components/Selector";
import { Fetcher } from "../components/Fetcher";
import { LogEvent } from "../models/LogEvents";
import { FilterContainer } from "../components/FilterContainer";
import { DEFAULT_PAGING_SIZE, MAX_TABLE_HEIGHT, MIN_TABLE_HEIGHT } from "../constants";
import { Pagination } from "../components/Pagination";
import { TwilioDetailsLogListContainer } from "../components/TwilioDetailsLogListContainer";
import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { MessageCard } from "../components/MessageCard";
import Typography from "@material-ui/core/Typography";
import {
	getTwilioChannelLogsPage,
	getTwilioServicesList,
	getTwilioChannelLogJson,
} from "../services/api/twilioChannelLogs.api";
import {
	TwilioChannelResponse,
	GetChatServicesResponse,
	ChannelLogsFilterObject,
	TwilioChannelListResponse,
} from "../models/TwilioModels";
import moment from "moment";
import { DateDisplay } from "../components/DateDisplay";
import queryString from "query-string";
import { store } from "../globalState/StateProvider";
import {
	setChannelLogsFiltersLocalStorageValue,
	getChannelLogsFiltersLocalStorageValue,
	resetChannelLogsFiltersLocalStorageValue,
} from "../globalState/LocalStorage/TwilioLocalStorageAccessors";

interface ChannelLogsPageProps {
	// subscription id to fetch details
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	tableWrapper: {
		minHeight: MIN_TABLE_HEIGHT,
		maxHeight: MAX_TABLE_HEIGHT,
		maxWidth: 1200,
		overflow: "auto",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	tableTitle: {
		zIndex: 1,
	},
}));

const renderList = (
	response: TwilioChannelResponse,
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
	subscriptionId: string,
	serviceSid: string,
	accountSid: string,
	productId: string,
) => {
	function loadJson(sid: string) {
		return getTwilioChannelLogJson(sid, serviceSid, productId, subscriptionId, accountSid);
	}
	let events: LogEvent[] = [];
	if (response.success && response.channelResponse) {
		const channelResponse: TwilioChannelListResponse = JSON.parse(response.channelResponse);

		events = channelResponse.channels.map((channel) => {
			const { channelSid, dateCreated, dateUpdated, channelJson, ...innerProps } = channel;

			return {
				sid: channelSid,
				eventDate: moment(dateCreated).toISOString(),
				message: channelJson,
				dateUpdated: DateDisplay({ date: dateUpdated, fontSize: 14 }),
				source: "channelList",
				...innerProps,
			};
		});
	}
	const query = queryString.parse(window.location.search);
	return (
		<div>
			{response.success ? (
				events.length > 0 || query.__sid !== undefined ? (
					<div>
						<div className={classes.tableWrapper}>
							<TwilioDetailsLogListContainer
								loadJson={loadJson}
								productId={productId}
								accountSid={accountSid}
								subscriptionId={subscriptionId}
								logs={events}
								pageName={"TwilioChannelList"}
							></TwilioDetailsLogListContainer>
						</div>
						{events.length > 0 ? (
							<Pagination
								pageNumber={pageNumber}
								rowsPerPage={rowsPerPage}
								setPageFunction={setPageNumber}
								setRowsPerPageFunction={setRowsPerPage}
								currentPageLength={events.length}
							></Pagination>
						) : (
							<div></div>
						)}
					</div>
				) : (
					<MessageCard message="No Events Found." />
				)
			) : (
				<MessageCard message="An error has occurred while retrieving logs. This can happen if the request is trying to retrieve too many logs at once. Try narrowing the request and try again. If the problem persists please contact an administrator." />
			)}
		</div>
	);
};

//Statuses to be displayed in the status selector.
const statuses: SelectorDisplayItemProps[] = [
	{
		displayString: "Any",
		valueString: "Any",
	},
	{
		valueString: "ACTIVE",
		displayString: "Active",
	},
	{
		valueString: "INACTIVE",
		displayString: "Inactive",
	},
];

/**
 * This page is used to get the channel logs for an MSO.
 * @param props
 */
export const ChannelLogsPage = (props: ChannelLogsPageProps) => {
	const classes = useStyles();
	const { environments, subscription } = React.useContext(store);

	const environmentsList = useMemo(
		() => (subscription && subscription!.subscription!.subscriptionId === props.subscriptionId ? environments : []),
		[environments, props.subscriptionId, subscription],
	);

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const [initialService, setInitialServiceSid] = React.useState("");
	const [services, setServices] = React.useState<SelectorDisplayItemProps[]>();
	const [update, forceUpdate] = React.useState(false);
	const [isLoaded, setIsLoaded] = React.useState(false);
	const filterObject: ChannelLogsFilterObject = getChannelLogsFiltersLocalStorageValue(props.subscriptionId);

	function UpdateFilters(newFilterObject) {
		setChannelLogsFiltersLocalStorageValue(props.subscriptionId, newFilterObject);
		setPageNumber(0);
		forceUpdate(!update);
	}

	function ResetFilters() {
		forceUpdate(!update);
		setPageNumber(0);
		return resetChannelLogsFiltersLocalStorageValue(props.subscriptionId);
	}

	const fetchServices = useCallback(
		async function (environment) {
			const response = await getTwilioServicesList(props.productId, props.subscriptionId, environment);
			const services: GetChatServicesResponse[] = response ? response.data : [];
			const newServices: SelectorDisplayItemProps[] = [];

			newServices.push(
				...services.map((item) => {
					return {
						displayString: item.serviceName,
						valueString: item.serviceSid,
					};
				}),
			);

			return newServices;
		},
		[props.productId, props.subscriptionId],
	);

	React.useEffect(() => {
		const fetch = async () => {
			if (environmentsList.length > 0) {
				const serviceList = await fetchServices(
					filterObject.selectedEnvironment !== "" ? filterObject.selectedEnvironment : environmentsList[0].valueString!,
				);
				setServices(serviceList);
				setInitialServiceSid(serviceList.length > 0 ? serviceList[0].valueString! : "");
				setIsLoaded(true);
			}
		};
		fetch();
	}, [environments, environmentsList, fetchServices, filterObject.selectedEnvironment]);

	const channelLogsRequest = {
		startDate: filterObject.startDate,
		endDate: filterObject.endDate,
		serviceSid: filterObject.selectedService === "" ? initialService : filterObject.selectedService,
		accountSid:
			!filterObject.selectedEnvironment && environmentsList.length > 0
				? environmentsList[0].valueString!
				: filterObject.selectedEnvironment,
		messageCount: filterObject.messagesCount,
		memberCount: filterObject.memberCount,
		status: filterObject.status,
		pageSize: rowsPerPage,
		pageNumber: pageNumber,
	};

	function loadList() {
		if (isLoaded && channelLogsRequest.serviceSid !== "") {
			return (
				<div>
					<Fetcher
						action={getTwilioChannelLogsPage(
							channelLogsRequest,
							props.productId,
							props.subscriptionId,
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
						)}
					>
						{(response) =>
							renderList(
								response.data,
								classes,
								setPageNumber,
								setRowsPerPage,
								pageNumber,
								rowsPerPage,
								props.subscriptionId,
								filterObject.selectedService,
								filterObject.selectedEnvironment !== ""
									? filterObject.selectedEnvironment
									: environmentsList[0].valueString!,
								props.productId,
							)
						}
					</Fetcher>
				</div>
			);
		}
	}

	return (
		<div data-testid={"subscription-twilio-channel-list-page"}>
			<Typography className={classes.title} variant="h4">
				Channel Logs
			</Typography>
			{environmentsList.length > 0 && services && (
				<FilterContainer
					fetchEnvironmentServices={fetchServices}
					environmentFilter={{
						environments: environmentsList,
						selectedEnvironment:
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
					}}
					environmentServices={services}
					statusFilter={{ statuses: statuses, selectedStatus: filterObject.status }}
					dateFilter={{
						startDate: filterObject.startDate,
						endDate: filterObject.endDate,
						refreshDateTimespan: filterObject.refreshDateTimespan,
					}}
					channelLogFilters={{
						memberCount: filterObject.memberCount,
						messagesCount: filterObject.messagesCount,
						selectedService: filterObject.selectedService,
					}}
					onUpdate={UpdateFilters}
					onReset={ResetFilters}
				></FilterContainer>
			)}
			{loadList()}
		</div>
	);
};
